// src/views/whatsappInbox/utils/MediaGroup.tsx
import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { useAppDispatch, useAppSelector } from '../../../hooks/hooks';
import { 
  cacheMedia, 
  openMediaPreview 
} from '../../../store/slices/mediaSlice';
import { MessageMedia } from '../components/MessageMedia';
import { 
  getGridConfig, 
  getMediaItemStyle, 
  getMediaGroupId,
  shouldShowMoreIndicator,
  getMoreIndicatorText
} from '../utils/mediaGroupingUtil';
import logger, { Logger } from '../../../utils/logger';
import { DownloadCloud } from 'lucide-react';
import toast from "react-hot-toast";

// Define props interface for the styled component
interface MediaGroupContainerProps {
  cols: number;
  rows: number;
}

// Styled components with stronger specificity
const MediaGroupContainer = styled.div<MediaGroupContainerProps>`
  && {
    display: grid !important;
    grid-template-columns: repeat(${props => props.cols}, 1fr) !important;
    grid-template-rows: repeat(${props => props.rows}, 1fr) !important;
    width: 100% !important;
    max-width: 300px !important;
    gap: 2px !important;
    border-radius: 8px !important;
    overflow: hidden !important;
    position: relative !important;
    margin-bottom: 8px !important;
  }
`;

const MediaGroupWrapper = styled.div`
  && {
    display: flex !important;
    align-items: flex-start !important;
    gap: 12px !important;
    margin: 8px 0 !important;
  }
`;

const MediaItemWrapper = styled.div`
  && {
    position: relative !important;
    overflow: hidden !important;
    min-height: 120px !important;
    border-radius: 0 !important;
  }
`;

const MoreIndicator = styled.div`
  && {
    position: absolute !important;
    bottom: 0 !important;
    right: 0 !important;
    background: rgba(0, 0, 0, 0.7) !important;
    color: white !important;
    padding: 4px 8px !important;
    font-size: 12px !important;
    font-weight: bold !important;
    z-index: 10 !important;
  }
`;

const DownloadAllButton = styled.button`
  && {
    background: rgba(0, 0, 0, 0.6) !important;
    color: white !important;
    border: none !important;
    border-radius: 50% !important;
    width: 36px !important;
    height: 36px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    cursor: pointer !important;
    flex-shrink: 0 !important;
    align-self: flex-start !important;
    transition: all 0.2s ease !important;
    
    &:hover {
      background: rgba(0, 0, 0, 0.8) !important;
      transform: scale(1.05) !important;
    }

    &:active {
      transform: scale(0.95) !important;
    }
  }
`;

interface MediaGroupProps {
  messages: any[];
}

export const MediaGroup: React.FC<MediaGroupProps> = ({ messages }) => {
  const dispatch = useAppDispatch();
  const [loadingMedia, setLoadingMedia] = useState<{[key: string]: boolean}>({});
  const [isDownloadingAll, setIsDownloadingAll] = useState(false);
  
  // Display first 4 messages only
  const displayMessages = messages.slice(0, 4);
  const groupSize = messages.length;
  
  // Get layout configuration
  const { columns, rows } = getGridConfig(groupSize);
  
  // Generate a unique group ID
  const groupId = getMediaGroupId(messages);
  
  logger.log("MediaGroup - Rendering with grid:", { 
    groupId, 
    size: groupSize,
    columns,
    rows
  });
  
  const handleMediaDownload = useCallback(async (message: any): Promise<string | null> => {
    if (!message._id) {
      logger.log("MediaGroup - Cannot download media - missing message ID");
      return null;
    }
    
    logger.log("MediaGroup - Downloading media for message:", message._id);
    setLoadingMedia(prev => ({ ...prev, [message._id]: true }));
    
    try {
      // Import and use the actual service
      const { downloadChatMedia } = await import("../../../services/whatsapp");
      
      let mediaId = message?.message?.id;
      if (!mediaId) {
        logger.log("MediaGroup - Media ID not found in message:", message);
        throw new Error("Media ID not found");
      }
      
      logger.log("MediaGroup - Using mediaId:", mediaId);
      let formdata = new FormData();
      formdata.append("mediaId", mediaId);
      
      const response = await downloadChatMedia(formdata);
      logger.log("MediaGroup - Download response received", { 
        hasData: !!response.data 
      });
      
      if (response.data) {
        const blob = new Blob([response.data], {
          type: message.messageType === "document"
            ? "application/pdf"
            : message.message.mime_type,
        });
        
        const url = URL.createObjectURL(blob);
        logger.log("MediaGroup - Created blob URL:", url);
        
        // Cache in Redux store
        dispatch(cacheMedia({
          messageId: message._id,
          url,
          type: message.messageType || 'unknown',
          mimeType: message.message?.mime_type || 'application/octet-stream'
        }));
        
        logger.log("MediaGroup - Media cached in Redux for:", message._id);
        return url; // Return the URL
      }
      
      return null;
    } catch (error) {
      logger.log("MediaGroup - Error downloading media:", error);
      return null;
    } finally {
      setLoadingMedia(prev => ({ ...prev, [message._id]: false }));
    }
  }, [dispatch]);
  
  // Handle downloading all media in the group

const handleDownloadAll = useCallback(async () => {
  setIsDownloadingAll(true);
  logger.log("MediaGroup - Starting download all for group:", groupId);
  
  try {
    // Create an array of download promises
    const downloads = messages.map(async (message) => {
      try {
        // This function now returns a URL or null
        const url = await handleMediaDownload(message);
        
        // Only attempt download if URL is returned
        if (url) {
          // Create a temporary link element
          const link = document.createElement('a');
          link.href = url;
          link.download = message.message?.filename || `${message.messageType || 'file'}-${Date.now()}`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          return { messageId: message._id, success: true };
        }
        
        return { messageId: message._id, success: false };
      } catch (error) {
        return { messageId: message._id, success: false, error };
      }
    });
    
    // Wait for all downloads to complete
    const results = await Promise.all(downloads);
    
    const successful = results.filter(r => r.success).length;
    logger.log(`MediaGroup - Downloaded ${successful} of ${messages.length} items`);
    
    if (successful === messages.length) {
      toast.success(`Downloaded all ${messages.length} media items`);
    } else {
      toast.success(`Downloaded ${successful} of ${messages.length} media items`);
    }
  } catch (error) {
    logger.log("MediaGroup - Error in download all:", error);
    toast.error("Failed to download some media items");
  } finally {
    setIsDownloadingAll(false);
  }
}, [groupId, messages, handleMediaDownload]);
  
  // Handle preview with group awareness
  const handlePreview = useCallback((messageId: string) => {
    // Open the preview with group context
    dispatch(openMediaPreview(messageId));
    
    // Set group context in Redux for navigation
    dispatch({
      type: 'media/setPreviewGroupContext',
      payload: {
        groupId,
        messageIds: messages.map(msg => msg._id)
      }
    });
  }, [dispatch, groupId, messages]);
  
  // Debugging - log what we're rendering
  logger.log(`MediaGroup - Rendering grid with ${columns} columns, ${rows} rows and ${displayMessages.length} items`);
  
  return (
   
  <MediaGroupWrapper>
    <div style={{ position: 'relative' }}>
      <MediaGroupContainer cols={columns} rows={rows}>
        {displayMessages.map((message, index) => {
          logger.log(`MediaGroup - Rendering item ${index} in grid`);
          
          return (
            <MediaItemWrapper key={message._id}>
            <MessageMedia
              message={message}
              onDownload={async () => {
                // Make sure we return the URL from handleMediaDownload
                return handleMediaDownload(message);
              }}
              onPreview={() => handlePreview(message._id)}
              integrationInfo={{}}
              thumbnailMode={true}
              isInGroup={true}
            />
          </MediaItemWrapper>
          );
        })}
      </MediaGroupContainer>
      
      {shouldShowMoreIndicator(groupSize) && (
        <MoreIndicator>
          {getMoreIndicatorText(groupSize)}
        </MoreIndicator>
      )}
    </div>
    
    <DownloadAllButton 
      onClick={handleDownloadAll}
      disabled={isDownloadingAll}
      title="Download all media"
    >
      {isDownloadingAll ? (
        <span className="loading-spinner" style={{ width: '16px', height: '16px' }}></span>
      ) : (
        <DownloadCloud size={16} />
      )}
    </DownloadAllButton>
  </MediaGroupWrapper>
);

};

export default MediaGroup;